import { DEFAULT_USER_DETAILS } from '@common/constants'
import { isPlaceholderPhone } from '@lib/utils/identifier'
import { enc } from 'crypto-js'
import SHA256 from 'crypto-js/sha256'

// Outschool signups hold a placeholder address on this domain, like ours on
// `DEFAULT_USER_DETAILS.emailDomain`. Mirrors `DUMMY_OUTSCHOOL_DOMAIN`.
const OUTSCHOOL_DUMMY_EMAIL_DOMAIN = '@outschool-student.com'

type MetaUser = {
  id: number
  parentName?: string | null
  email?: string | null
  phone?: string | null
  city?: string | null
}

// Meta's ct, fn and ln rule keeps letters only, so spaces, digits and
// punctuation go. Built at runtime because the Babel bundled with Next 11 cannot
// compile a \p{...} regex literal and fails the build.
const lettersOnly = (value: string): string =>
  value.toLowerCase().replace(new RegExp('[^\\p{L}]', 'gu'), '')

/**
 * Advanced-matching user data for the Meta Pixel's `fbq('init')`.
 *
 * Mirrors `build_user_data` in `api/apps/meta/utils.py` key for key and digest
 * for digest: the pixel and the Conversions API describe the same person, and a
 * key the two derive differently is one Meta silently cannot match on. The
 * tests pin the backend's digests.
 */
export const buildMetaUserData = (
  user: MetaUser,
  countryCode?: string | null,
  cookies: Record<string, string> = {}
): Record<string, string> => {
  const email = (user.email ?? '').trim().toLowerCase()
  const isPlaceholderEmail =
    email.endsWith(DEFAULT_USER_DETAILS.emailDomain) ||
    email.endsWith(OUTSCHOOL_DUMMY_EMAIL_DOMAIN)

  // The email-signup placeholder is a number nobody answers, so it would only
  // add a key matching nobody.
  const phone = isPlaceholderPhone(user.phone)
    ? ''
    : (user.phone ?? '').replace(/\D/g, '').replace(/^0+/, '')

  const country = (countryCode ?? '').toLowerCase().replace(/[^a-z]/g, '')

  // The parent's name, not the student's: the email and phone are the
  // parent's, so the student's name would describe a second person. The last
  // word is the last name, the rest the first name.
  const nameParts = (user.parentName ?? '').trim().split(/\s+/).filter(Boolean)
  const firstName = nameParts
    .slice(0, Math.max(nameParts.length - 1, 1))
    .join(' ')
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''

  const normalized = {
    em: isPlaceholderEmail ? '' : email,
    ph: phone,
    fn: lettersOnly(firstName),
    ln: lettersOnly(lastName),
    // Anything but an ISO alpha-2 code hashes to a value matching nobody.
    country: country.length === 2 ? country : '',
    ct: lettersOnly(user.city ?? ''),
    external_id: String(user.id),
  }

  const userData: Record<string, string> = {}
  for (const [key, value] of Object.entries(normalized)) {
    // crypto-js hashes only strings - a number is read as its own byte-array
    // type and every value comes out as one fixed digest - so each value here
    // must be a string. Empty ones are omitted, since hash('') matches nobody.
    if (value) userData[key] = SHA256(value).toString(enc.Hex)
  }

  // The pixel's own cookies go in plain text; hashing them breaks matching.
  if (cookies._fbc) userData.fbc = cookies._fbc
  if (cookies._fbp) userData.fbp = cookies._fbp

  return userData
}
